import { useCallback, useMemo, useRef, useState } from "react"

import { Dropdown, Menu, type ItemType } from "@/atoms"
import { preventEventBubbling } from "@/helpers/utils"
import { useClickOutside } from "@/hooks/useClickOutside"
import { useToggle } from "@/hooks/useToggle"
import { useTranslation } from "@/hooks/useTranslation"
import { ButtonQuickFilter } from "./components/ButtonQuickFilter"
import { FooterQuickFilter } from "./components/FooterQuickFilter"
import { IconButtonQuickFilter } from "./components/IconButtonQuickFilter"
import { SearchQuickFilter } from "./components/SearchQuickFilter"

import styles from "./QuickFilterDropdown.module.scss"

import type { QuickFilterDropdownProps } from "."

export const QuickFilterDropdown = (props: QuickFilterDropdownProps) => {
  const { children, items: _items, allowSearch, isPlaceholder, ...rest } = props

  const { value: open, toggle, setValue } = useToggle()
  const [search, setSearch] = useState("")
  const { t } = useTranslation("translation", { keyPrefix: "general" })
  const items: ItemType[] = useMemo(() => {
    const searchValue = search.toLowerCase()

    const filteredItems = (_items || []).filter((item) => {
      if (!search) return true
      if (!item) return false

      if ("label" in item && typeof item.label !== "function")
        return `${item.label}`.toLocaleLowerCase().indexOf(searchValue) !== -1

      return true
    })

    return [
      ...SearchQuickFilter(props, setSearch),
      {
        key: "menu",
        className: "menu-item-quick-filter-dropdown-component",
        label: <Menu rootClassName={styles.menuRoot} items={filteredItems} />
      },
      ...FooterQuickFilter({ toggle, ...props }, filteredItems, t)
    ]

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_items, search, toggle])

  const ref = useRef(null)
  const close = useCallback(() => setValue(false), [setValue])

  useClickOutside(ref, close)

  const isChildrenExist = !!children

  return (
    <div className={styles.container}>
      <Dropdown
        open={open}
        rootClassName={styles.root}
        menu={{
          items,
          onClick: ({ domEvent }) => preventEventBubbling(domEvent)
        }}
      >
        <span ref={ref}>
          {isChildrenExist && (
            <ButtonQuickFilter onClick={toggle} bold={!isPlaceholder} {...rest}>
              {children}
            </ButtonQuickFilter>
          )}
          {!isChildrenExist && (
            <IconButtonQuickFilter onClick={toggle} {...rest} />
          )}
        </span>
      </Dropdown>
    </div>
  )
}
